import type {Activity, Order} from '@wix/ambassador-ep-orders/types'

export type SavedCard = {
  brand: string
  last4: string
}

const BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  'master card': 'Mastercard',
  amex: 'Amex',
  'american express': 'Amex',
  american_express: 'Amex',
  discover: 'Discover',
}

const LAST4_IN_TEXT = /(?:[•*xX]{3,4}|ending (?:in|with))\s*(\d{4})/i

const formatCardBrand = (brand?: string | null) => {
  if (!brand) {
    return 'Card'
  }

  return BRAND_LABELS[brand.toLowerCase()] ?? brand
}

const toSavedCard = (brand?: string | null, last4?: string | null): SavedCard | undefined => {
  const digits = last4?.replace(/\D/g, '').slice(-4)

  if (!digits || digits.length !== 4) {
    return undefined
  }

  return {
    brand: formatCardBrand(brand),
    last4: digits,
  }
}

const asNonEmptyString = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  if (typeof value !== 'string') {
    return undefined
  }

  const trimmed = value.trim()

  return trimmed || undefined
}

const parseCardFromText = (brand?: string | null, text?: string | null) => {
  if (!text) {
    return undefined
  }

  const match = text.match(LAST4_IN_TEXT)
  const last4 = match?.[1]

  return toSavedCard(brand, last4)
}

const getSavedCardFromRecord = (value: unknown, depth = 0): SavedCard | undefined => {
  if (value == null || depth > 8) {
    return undefined
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const fromItem = getSavedCardFromRecord(item, depth + 1)

      if (fromItem) {
        return fromItem
      }
    }

    return undefined
  }

  if (typeof value !== 'object') {
    return undefined
  }

  const record = value as Record<string, unknown>
  const last4 = asNonEmptyString(record.lastFourDigits ?? record.last_four_digits)
  const brand = asNonEmptyString(record.brand)

  if (last4) {
    return toSavedCard(brand, last4)
  }

  for (const nested of Object.values(record)) {
    if (nested && typeof nested === 'object') {
      const fromNested = getSavedCardFromRecord(nested, depth + 1)

      if (fromNested) {
        return fromNested
      }
    }
  }

  return undefined
}

const getSavedCardFromActivity = (activity: Activity) => {
  const authorized =
    activity.authorizedPaymentCreated ??
    activity.authorizedPaymentCaptured ??
    activity.authorizedPaymentVoided
  const fromAuthorized = toSavedCard(authorized?.brand, authorized?.lastFourDigits)

  if (fromAuthorized) {
    return fromAuthorized
  }

  const regular =
    activity.paymentPending?.regular ??
    activity.paymentCanceled?.regular ??
    activity.paymentDeclined?.regular
  const fromRegular = toSavedCard(
    regular?.creditCardDetails?.brand,
    regular?.creditCardDetails?.lastFourDigits,
  )

  if (fromRegular) {
    return fromRegular
  }

  const refunded =
    activity.paymentRefunded?.payment?.regular ??
    activity.paymentRefundFailed?.payment?.regular ??
    activity.refundInitiated?.payments?.[0]?.regular
  const fromRefunded = toSavedCard(refunded?.brand, refunded?.lastFourDigits)

  if (fromRefunded) {
    return fromRefunded
  }

  const fromSavedMethod = parseCardFromText(
    activity.savedPaymentMethod?.name,
    activity.savedPaymentMethod?.description ?? activity.savedPaymentMethod?.name,
  )

  if (fromSavedMethod) {
    return fromSavedMethod
  }

  return getSavedCardFromRecord(activity)
}

export const getSavedCardFromOrder = (order?: Order | null): SavedCard | undefined => {
  if (!order) {
    return undefined
  }

  for (const activity of order.activities ?? []) {
    const fromActivity = getSavedCardFromActivity(activity)

    if (fromActivity) {
      return fromActivity
    }
  }

  const orderWithPayments = order as Order & {payment?: unknown; payments?: unknown}

  return getSavedCardFromRecord({
    activities: order.activities,
    payments: orderWithPayments.payments,
    payment: orderWithPayments.payment,
  })
}

export const hasCapturedCreditCard = (order?: Order | null) =>
  Boolean(getSavedCardFromOrder(order)) ||
  (order?.activities ?? []).some(
    (activity) =>
      Boolean(activity.authorizedPaymentCreated) ||
      Boolean(activity.authorizedPaymentCaptured) ||
      Boolean(activity.authorizedPaymentVoided) ||
      Boolean(activity.paymentPending?.regular?.creditCardDetails) ||
      Boolean(activity.paymentCanceled?.regular?.creditCardDetails) ||
      Boolean(activity.paymentDeclined?.regular?.creditCardDetails) ||
      Boolean(activity.paymentRefunded?.payment?.regular) ||
      Boolean(activity.savedPaymentMethod),
  )
